import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import {
  getRefreshToken,
  removeRefreshToken,
  setRefreshToken,
} from "./refreshTokenStorage"
import jwtDecode from "./jwtDecode"
import type { TokenResponse } from "./model"
import { clearAbly } from "@/base/ably"
import { trackUser } from "@/monitoring"
import type { Uuid } from "@/utils"

export type State = {
  isLoading: boolean
  userId: Uuid | null
  roles: string[]
  token: string | null
  isUnverifiedExpired?: true
  tenantSlug: string | null
}

const initialState: State = {
  isLoading: true,
  userId: null,
  roles: [],
  token: null,
  tenantSlug: null,
}

const endSession = (state: State) => {
  state.userId = null
  state.roles = []
  state.token = null
  state.tenantSlug = null
  state.isLoading = false
  delete state.isUnverifiedExpired

  clearAbly()
}

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateTokens(
      state,
      {
        payload: { token, refreshToken, isUnverifiedExpired },
      }: PayloadAction<TokenResponse>,
    ) {
      const { userId, roles } = jwtDecode(token)

      // A refresh can return another tab's newer sign-in, as someone else
      // whose workspaces this one may not belong to
      if (userId !== state.userId) {
        state.tenantSlug = null
      }
      state.userId = userId
      state.roles = roles
      state.token = token
      state.isLoading = false
      if (isUnverifiedExpired) {
        state.isUnverifiedExpired = true
      }

      setRefreshToken(refreshToken)

      void trackUser(userId)
    },
    /**
     * Ends the session and forgets the stored refresh token. Given the token this session used, it
     * forgets only that one: another tab may since have stored a newer one, which is still alive.
     */
    clearAuth(
      state,
      { payload: usedToken }: PayloadAction<string | undefined>,
    ) {
      endSession(state)

      if (usedToken === undefined || getRefreshToken() === usedToken) {
        removeRefreshToken()
      }
    },
    /**
     * Ends the session but keeps the stored refresh token, for when the server declined to answer
     * rather than rejecting the credential: a rate limit, a dropped connection.
     */
    suspendAuth(state) {
      endSession(state)
    },
    setTenantSlug(state, action: PayloadAction<string>) {
      state.tenantSlug = action.payload
    },
  },
  selectors: {
    getToken: (state) => state.token,
    getTokenAvailable: (state) => !!state.token,
    getLoading: (state) => state.isLoading,
    getUnverifiedExpired: (state) => state.isUnverifiedExpired,
    getUserId: (state) => state.userId,
    getRoles: (state) => state.roles,
    getTenantSlug: (state) => state.tenantSlug,
  },
})

export const { updateTokens, clearAuth, suspendAuth, setTenantSlug } =
  slice.actions
export const {
  getToken,
  getTokenAvailable,
  getLoading,
  getUserId,
  getRoles,
  getUnverifiedExpired,
  getTenantSlug,
} = slice.selectors

export default slice
